import React, {KeyboardEvent, ReactNode, useEffect, useState} from 'react';
import classNames from 'classnames';
import {
    CheckIcon,
    ChevronDownIcon,
    LayoutGridIcon,
    ListIcon,
    SearchIcon,
    XIcon
} from 'lucide-react';
import {Dropdown, DropdownItem, DropdownMenu, DropdownToggle} from 'reactstrap';
import Icon from '../shared/Icon.tsx';
import SegmentedControl from './SegmentedControl.tsx';

export type ViewMode = 'grid' | 'list';

interface FilterSearchProps {
    value: string;
    // Called on Enter and on clear, not on every keystroke.
    onChange: (value: string) => void;
    placeholder?: string;
    'aria-label'?: string;
    className?: string;
}

export const FilterSearch: React.FC<FilterSearchProps> = ({
    value,
    onChange,
    placeholder = 'Search',
    className,
    ...rest
}) => {
    const [draft, setDraft] = useState(value);

    useEffect(() => {
        setDraft(value);
    }, [value]);

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            onChange(draft);
        }
    };

    return (
        <div className={classNames('mx-search', className)}>
            <Icon icon={SearchIcon} size="s" className="mx-search-icon" />
            <input
                type="search"
                className="mx-search-input"
                placeholder={placeholder}
                aria-label={rest['aria-label'] ?? placeholder}
                value={draft}
                maxLength={512}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
            />
            {draft && (
                <button
                    type="button"
                    className="btn btn-tertiary btn-sm btn-icon-only mx-search-clear"
                    aria-label="Clear search"
                    onClick={() => {
                        setDraft('');
                        onChange('');
                    }}
                >
                    <Icon icon={XIcon} size="xs" />
                </button>
            )}
        </div>
    );
};

interface FilterMenuProps {
    label: string;
    // The chosen value, shown in the button when set.
    value?: string | null;
    children: ReactNode;
    // A list of choices rather than a menu of actions: its items are options (DropdownItem then gives them
    // role="option", and arrow keys still move between them). multiple: several can be chosen.
    choices?: 'single' | 'multiple';
    className?: string;
}

// A filter that opens a menu; when a value is set the button says so and
// takes the selected outline.
export const FilterMenu: React.FC<FilterMenuProps> = ({
    label,
    value,
    children,
    choices,
    className
}) => {
    const [open, setOpen] = useState(false);

    return (
        <Dropdown
            isOpen={open}
            toggle={() => setOpen(o => !o)}
            menuRole={choices ? 'listbox' : undefined}
            className={classNames('mx-filter-menu', className)}
        >
            <DropdownToggle
                tag="button"
                type="button"
                className={classNames('btn btn-secondary mx-filter-toggle', {
                    'mx-filter-set': !!value
                })}
            >
                {value ? `${label}: ${value}` : label}
                <Icon icon={ChevronDownIcon} size="xs" />
            </DropdownToggle>
            <DropdownMenu
                className="mx-menu"
                aria-label={label}
                aria-multiselectable={choices === 'multiple' ? true : undefined}
            >
                {children}
            </DropdownMenu>
        </Dropdown>
    );
};

interface FilterSortProps<T extends string | number> {
    value: T;
    onChange: (value: T) => void;
    options: {value: T; label: string}[];
    className?: string;
}

// The sort, at the end of a filter bar: "Sort: Newest". Always has a value,
// so it never takes a filter's selected outline.
export const FilterSort = <T extends string | number>({
    value,
    onChange,
    options,
    className
}: FilterSortProps<T>) => {
    const [open, setOpen] = useState(false);
    const current = options.find(option => option.value === value) ?? options[0];

    return (
        <Dropdown
            isOpen={open}
            toggle={() => setOpen(o => !o)}
            menuRole="listbox"
            className={classNames('mx-filter-menu', className)}
        >
            <DropdownToggle
                tag="button"
                type="button"
                className="btn btn-tertiary mx-filter-toggle"
                aria-label={`Sort: ${current.label}`}
            >
                Sort: {current.label}
                <Icon icon={ChevronDownIcon} size="xs" />
            </DropdownToggle>
            <DropdownMenu end className="mx-menu" aria-label="Sort">
                {options.map(option => (
                    <DropdownItem
                        key={option.value}
                        aria-selected={option.value === current.value}
                        className="mx-menu-item mx-check-item"
                        onClick={() => onChange(option.value)}
                    >
                        <span className="mx-check-mark">
                            {option.value === current.value && (
                                <Icon icon={CheckIcon} size="xs" />
                            )}
                        </span>
                        <span className="mx-menu-item-label">{option.label}</span>
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </Dropdown>
    );
};

interface FilterBarProps {
    search?: FilterSearchProps;
    // FilterMenu buttons and segmented controls.
    children?: ReactNode;
    // Shown when any filter is set.
    onClear?: () => void;
    sort?: ReactNode;
    view?: {value: ViewMode; onChange: (view: ViewMode) => void};
    className?: string;
}

/*
 * One filter bar: a search field that also matches tags, filter menus that
 * show their value when set, a sort, a view switch and Clear.
 */
const FilterBar: React.FC<FilterBarProps> = ({
    search,
    children,
    onClear,
    sort,
    view,
    className
}) => (
    <div className={classNames('mx-filter-bar', className)}>
        {search && <FilterSearch {...search} />}
        {children}
        {onClear && (
            <button type="button" className="btn btn-tertiary" onClick={onClear}>
                Clear
            </button>
        )}
        <span className="mx-filter-spacer" />
        {sort}
        {view && (
            <SegmentedControl
                aria-label="View"
                size="sm"
                value={view.value}
                onChange={view.onChange}
                options={[
                    {
                        value: 'grid',
                        ariaLabel: 'Grid view',
                        label: <Icon icon={LayoutGridIcon} size="xs" />
                    },
                    {
                        value: 'list',
                        ariaLabel: 'List view',
                        label: <Icon icon={ListIcon} size="xs" />
                    }
                ]}
            />
        )}
    </div>
);

export default FilterBar;
